import React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Field, Form, Formik } from 'formik'
import { useStore } from 'react-redux'
import {
  GET_TICKET_CONFIGURATION_BY_SLUG_QUERY_KEY,
  useUpsertConfiguration,
} from 'src/api/queries/ticket-applications'
import { useTicketsContext } from 'src/reports/ticket-applications/context'
import { Button, Card, Checkbox, HStack, Input, Stack, Text } from 'src/ui'
import { useToast } from 'src/utils/toast'

const PDF_TITLE_MAX_CHAR_LENGTH = 120

function validatePdfTitle(value: string) {
  if (value.length > PDF_TITLE_MAX_CHAR_LENGTH) {
    return `The PDF Title must be less than ${PDF_TITLE_MAX_CHAR_LENGTH} characters.`
  }
}

function DetailsCard({
  title,
  truckDriverEnabled,
  truckLicenseEnabled,
  truckNameEnabled,
}: Readonly<{
  title: string
  truckDriverEnabled: boolean
  truckLicenseEnabled: boolean
  truckNameEnabled: boolean
}>) {
  const { detailsIsEditing, setDetailsIsEditing } = useTicketsContext()
  const companySlug = useStore().getState().settings.companySlug
  const queryClient = useQueryClient()
  const handleToast = useToast()

  const { mutateAsync: upsertConfigAsync, isPending: isLoading } = useUpsertConfiguration({
    slug: companySlug,
  })

  if (detailsIsEditing) {
    return (
      <Card>
        <Formik
          initialValues={{
            title: title,
            truckDriverEnabled: truckDriverEnabled,
            truckLicenseEnabled: truckLicenseEnabled,
            truckNameEnabled: truckNameEnabled,
          }}
          onSubmit={({ title, truckDriverEnabled, truckLicenseEnabled, truckNameEnabled }) => {
            upsertConfigAsync({
              title: title,
              enabled: true,
              truckDriverEnabled: truckDriverEnabled,
              truckLicenseEnabled: truckLicenseEnabled,
              truckNameEnabled: truckNameEnabled,
            })
              .then(() => {
                queryClient.refetchQueries({
                  queryKey: [GET_TICKET_CONFIGURATION_BY_SLUG_QUERY_KEY],
                })
                handleToast({
                  title: 'Success',
                  description: 'Ticket Applications Report details updated',
                })
                setDetailsIsEditing(false)
              })
              .catch(() => {
                handleToast({
                  title: 'Error',
                  description: 'Failed to update Ticket Applications Report details',
                  status: 'error',
                })
              })
          }}
        >
          {({ handleSubmit, errors, isValid, dirty }) => (
            <Form onSubmit={handleSubmit}>
              <Stack spacing={8}>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Details</Text>
                  <HStack>
                    <Button
                      onClick={() => {
                        setDetailsIsEditing(false)
                      }}
                      size="sm"
                      isDisabled={isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="primary"
                      type="submit"
                      isDisabled={!isValid || isLoading || !dirty}
                      isLoading={isLoading}
                    >
                      Save
                    </Button>
                  </HStack>
                </HStack>
                <Stack spacing={3}>
                  <Text color="gray.500" fontSize="sm">
                    PDF Title
                  </Text>
                  <Field as={Input} id="tTitle" name="title" validate={validatePdfTitle} />
                  {errors?.title && <Text color="red">{errors.title}</Text>}
                </Stack>
              </Stack>
              <Stack spacing={2} pt={8}>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Delivery Vehicle Information</Text>
                </HStack>
                <Text fontSize="sm">
                  Select the delivery vehicle details to display on the report. If no details are
                  needed unselect all options.
                </Text>
                <Stack spacing={3}>
                  <Text color="gray.500" fontSize="sm" pt={6}>
                    Delivery Vehicle Options
                  </Text>
                  <Card boxShadow="none" border="1px" borderColor="gray.200" borderRadius="4" p={0}>
                    <HStack bg="gray.50" borderBottom="1px" borderColor="gray.200" p={4}>
                      <Text as="b" w="98%">
                        License #
                      </Text>
                      <Field
                        as={Checkbox}
                        defaultChecked={truckLicenseEnabled}
                        size="lg"
                        id="tTruckLicenseEnabled"
                        name="truckLicenseEnabled"
                      />
                    </HStack>
                    <HStack p={4}>
                      <Text as="b" w="98%">
                        Driver
                      </Text>
                      <Field
                        as={Checkbox}
                        defaultChecked={truckDriverEnabled}
                        size="lg"
                        id="tTruckDriverEnabled"
                        name="truckDriverEnabled"
                      />
                    </HStack>
                    <HStack bg="gray.50" borderTop="1px" borderColor="gray.200" p={4}>
                      <Text as="b" w="98%">
                        Vehicle Name
                      </Text>
                      <Field
                        as={Checkbox}
                        defaultChecked={truckNameEnabled}
                        size="lg"
                        id="tTruckNameEnabled"
                        name="truckNameEnabled"
                      />
                    </HStack>
                  </Card>
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      </Card>
    )
  } else {
    return (
      <Card>
        <Stack spacing={8}>
          <HStack justify="space-between">
            <Text fontWeight="bold">Details</Text>
            <Button
              size="sm"
              onClick={() => {
                setDetailsIsEditing(true)
              }}
            >
              Edit
            </Button>
          </HStack>
          <Stack spacing={3}>
            <Text color="gray.500" fontSize="sm">
              PDF Title
            </Text>
            <Text>{title}</Text>
          </Stack>
          <Stack spacing={3}>
            <Text color="gray.500" fontSize="sm">
              Delivery Vehicle Options
            </Text>
            <Text>
              <Checkbox isDisabled defaultChecked={truckLicenseEnabled} size="lg">
                License #
              </Checkbox>
            </Text>
            <Text>
              <Checkbox isDisabled defaultChecked={truckDriverEnabled} size="lg">
                Driver
              </Checkbox>
            </Text>
            <Text>
              <Checkbox isDisabled defaultChecked={truckNameEnabled} size="lg">
                Vehicle Name
              </Checkbox>
            </Text>
          </Stack>
        </Stack>
      </Card>
    )
  }
}

export default DetailsCard
